import React, { useState, useEffect } from 'react';
import './MemoryDAG.css';

const ZepMemory = () => {
  const [facts, setFacts] = useState([]);
  const [stats, setStats] = useState({ total_facts: 0, session_id: '', has_context: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch all facts from Zep
  const fetchFacts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/zep/facts');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFacts(data.facts || []);
        } else {
          setError('Failed to fetch facts');
        }
      } else {
        setError('Failed to connect to memory system');
      }
    } catch (err) {
      console.error('❌ Error fetching facts:', err);
      setError('Error fetching facts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch memory stats
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/zep/stats');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.stats);
        }
      }
    } catch (err) {
      console.error('❌ Error fetching stats:', err);
    }
  };

  // Search memory
  const searchMemory = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/zep/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: searchQuery,
          limit: 10
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSearchResults(data.results || []);
        }
      }
    } catch (err) {
      console.error('❌ Error searching memory:', err);
      setError('Search failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Clear memory
  const clearMemory = async () => {
    if (!window.confirm('Are you sure you want to clear all memory? This cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/zep/clear', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFacts([]);
          setSearchResults([]);
          setSearchQuery('');
          setStats({ total_facts: 0, session_id: '', has_context: false });
          alert('Memory cleared from cloud successfully!');
        } else {
          setError('Failed to clear memory: ' + (data.error || 'Unknown error'));
        }
      } else {
        throw new Error(`Clear failed: ${response.status}`);
      }
    } catch (err) {
      console.error('❌ Error clearing memory:', err);
      setError('Clear failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchFacts();
    fetchStats();
  }, []);

  // Search when query changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        searchMemory();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <div className="memory-system">
      {/* Header */}
      <div className="memory-header">
        <h2>🧠 Memory System</h2>
        <p>Stored conversations and facts from your interactions</p>

        {/* Stats */}
        <div className="memory-stats">
          <div className="stat-item">
            <span className="stat-number">{stats.total_facts}</span>
            <span className="stat-label">Facts Stored</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.session_id ? 'Active' : 'None'}</span>
            <span className="stat-label">Session</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="memory-section">
        <h3>🔍 Search Memory</h3>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search your memory..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="clear-search-btn"
            >
              Clear
            </button>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="search-results">
            <h4>Search Results ({searchResults.length})</h4>
            <div className="facts-list">
              {searchResults.map((result, index) => (
                <div key={index} className="fact-item search-result">
                  <span className="fact-content">{result.fact}</span>
                  {result.score && (
                    <span className="fact-score">Score: {result.score.toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* All Facts */}
      <div className="memory-section">
        <h3>📚 All Stored Facts</h3>
        {loading && <div className="loading">Loading...</div>}
        {error && <div className="error">{error}</div>}

        {facts.length === 0 && !loading ? (
          <div className="no-facts">
            <p>No facts stored yet.</p>
            <p>Start chatting and the AI will automatically remember your conversations!</p>
          </div>
        ) : (
          <div className="facts-list">
            {facts.map((fact, index) => (
              <div key={index} className="fact-item">
                <span className="fact-content">{fact}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="memory-actions">
        <button
          onClick={fetchFacts}
          disabled={loading}
          className="refresh-btn"
        >
          🔄 Refresh
        </button>
        <button
          onClick={clearMemory}
          disabled={loading || facts.length === 0}
          className="clear-btn"
        >
          🗑️ Clear Memory
        </button>
      </div>
    </div>
  );
};

export default ZepMemory; 